import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  AccountAttributeUpdateAction,
  addDescription,
  deleteDescription,
  updateDescription,
} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {CrmSyncStatusesEnum} from '../../../../models/api/crmSyncModel';
import {InlineEditingTextBox} from '../../../common/inlineEditing/inlineEditingTextBox';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from '../accountDetailsContext';
import {AccountInformationAttribute} from './accountInformationAttribute';

/*
 * Props.
 */

interface DescriptionProps {
  account: AccountModel;
  crmSyncEditLink?: string;
  crmSyncStatus?: CrmSyncStatusesEnum;
  shouldDisableActions?: boolean;
  updateAccountAttributeAsync: AsyncHandlerOf<AccountAttributeUpdateAction>;
  updateUnsavedAccount: HandlerOf<AccountAttributeUpdateAction>;
}

/*
 * Style.
 */

const StyledDescriptionDiv = styled.div`
  margin: 8px 0 7px 0;
`;

/*
 * Component.
 */

export const Description: FC<DescriptionProps> = (props) => {
  const {
    account: {id: accountId, description, externalInfo},
    crmSyncEditLink,
    crmSyncStatus,
    shouldDisableActions,
    updateAccountAttributeAsync,
    updateUnsavedAccount,
  } = props;
  const {onTrackEvent} = useAccountDetailsContext();

  if (description === undefined) {
    return null;
  }

  const isAddingDescription = description === '';

  return (
    <AccountInformationAttribute
      title={<Trans id="account-details-account-information-description">Description</Trans>}
      isFieldCrmSyncing={Boolean(crmSyncEditLink)}
      crmSyncEditLink={crmSyncEditLink}
      crmSyncSource={externalInfo?.source}
      crmSyncStatus={crmSyncStatus}
      shouldDisableActions={shouldDisableActions}
      renderValue={({isEditing, editedValue, onEdit, onEditSave, onEditCancel}) => {
        if (isEditing) {
          return (
            <InlineEditingTextBox
              value={editedValue}
              onEdit={onEdit}
              onEditSave={onEditSave}
              onEditCancel={onEditCancel}
            />
          );
        }

        if (isAddingDescription) {
          return <StyledDescriptionDiv>-</StyledDescriptionDiv>;
        }

        return <StyledDescriptionDiv>{description}</StyledDescriptionDiv>;
      }}
      currentAttributeValue={description}
      onSave={(newValue: string) => {
        const action = computeOnSaveAction(isAddingDescription, description, newValue);
        onTrackEvent(
          buildAccountDetailsEditPayload(
            accountId,
            isAddingDescription ? AccountDetailsEditActionsEnum.ADD : AccountDetailsEditActionsEnum.EDIT,
            AccountDetailsEditAttributesEnum.DESCRIPTION,
          ),
        );
        return updateAccountAttributeAsync(action);
      }}
      onDelete={() => {
        const action = deleteDescription(description);

        // No need to hit the backend if the user deletes the description before it's saved
        if (isAddingDescription) {
          return updateUnsavedAccount(action);
        }

        onTrackEvent(
          buildAccountDetailsEditPayload(
            accountId,
            AccountDetailsEditActionsEnum.DELETE,
            AccountDetailsEditAttributesEnum.DESCRIPTION,
          ),
        );
        return updateAccountAttributeAsync(action);
      }}
      shouldStartInEditMode={isAddingDescription}
    />
  );
};

/*
 * Helpers.
 */

function computeOnSaveAction(isAddingDescription: boolean, description: string, newValue: string) {
  if (isAddingDescription) {
    return addDescription(newValue);
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteDescription(description);
  }

  return updateDescription(description, newValue);
}
